/**
 * Building and checking a playbook's step tree. Pure functions over the catalog, so the builder stays
 * a thin view and every rule here is testable without rendering.
 */

import type {
	ActionStep,
	CatalogAction,
	CatalogParam,
	IfStep,
	RuleCondition,
	RuleDefinition,
	RuleStep,
	RuleTrigger,
	RulesCatalog
} from '@/types/rules.types';
import { emptyValueFor, isValueMissing, valueInputFor } from './value-input';

/** How deep "If" blocks may nest. The backend allows 6; 4 keeps a playbook readable. */
export const MAX_IF_DEPTH = 4;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const newId = (): string => crypto.randomUUID();

export const factsFor = (catalog: RulesCatalog, trigger: RuleTrigger) => catalog.facts.filter((fact) => fact.triggers.includes(trigger));
/** The facts an "after" event carries, for Try it (its Kim check isn't something that happened). */
export const eventFactsFor = (catalog: RulesCatalog, trigger: RuleTrigger) =>
	catalog.facts.filter((fact) => fact.group === 'What happened' && fact.id !== 'event.check' && fact.triggers.includes(trigger));
export const actionsFor = (catalog: RulesCatalog, trigger: RuleTrigger) =>
	catalog.actions.filter((action) => action.triggers.includes(trigger));

/** The decisions a trigger offers, in that trigger's words ("Hold it for your team" on an answer). */
export const outcomesFor = (catalog: RulesCatalog, trigger: RuleTrigger) =>
	catalog.outcomes.flatMap((outcome) => {
		const own = outcome.byTrigger?.[trigger];
		if (own === null) return [];
		return [{ ...outcome, ...(own ?? {}) }];
	});

export function newCondition(catalog: RulesCatalog, trigger: RuleTrigger): RuleCondition {
	const fact = factsFor(catalog, trigger)[0] ?? catalog.facts[0];
	const op = fact?.operators[0] ?? 'is';
	return { id: newId(), fact: fact?.id ?? 'message.text', op, value: emptyValueFor(valueInputFor(fact, op, catalog.operators)) };
}

/** The params an action starts with: each one's catalog default. */
export function defaultParams(action: CatalogAction): Record<string, unknown> {
	return Object.fromEntries(action.params.filter((param) => param.default !== undefined).map((param) => [param.key, param.default]));
}

export function newStep(kind: RuleStep['kind'], catalog: RulesCatalog, trigger: RuleTrigger): RuleStep {
	if (kind === 'if') return { id: newId(), kind: 'if', match: 'all', conditions: [newCondition(catalog, trigger)], then: [], else: [] };
	if (kind === 'action') {
		const action = actionsFor(catalog, trigger)[0] ?? catalog.actions[0];
		return { id: newId(), kind: 'action', action: action?.id ?? 'reply', params: action ? defaultParams(action) : {} };
	}
	return { id: newId(), kind: 'outcome', outcome: outcomesFor(catalog, trigger)[0]?.id ?? 'hand_to_kim' };
}

/** A copy with fresh ids all the way down, so the copy and the original never share a trace. */
export function cloneStep(step: RuleStep): RuleStep {
	if (step.kind === 'if') {
		return {
			...step,
			id: newId(),
			conditions: step.conditions.map((condition) => ({ ...condition, id: newId() })),
			then: step.then.map(cloneStep),
			else: step.else.map(cloneStep)
		};
	}
	return { ...step, id: newId() };
}

/**
 * Every step list reads: zero or more actions, then at most one ending step — a check (which splits
 * into If yes / Otherwise) or a decision. Nothing comes after the ending; the next steps go inside
 * the check's branches. The backend enforces the same order.
 */
export const isEnding = (step: RuleStep): boolean => step.kind === 'if' || isDecision(step);

/** A decision, or sending a comment to the Social Media Agent, which decides too (the backend's `engine.ts`). */
export const isDecision = (step: RuleStep): boolean => step.kind === 'outcome' || (step.kind === 'action' && step.action === 'social_agent');

/** The list's ending step: its first check or decision. In an old out-of-order list, steps follow it. */
export const endingOf = (steps: RuleStep[]): RuleStep | undefined => steps.find(isEnding);

/** How many steps sit after a check or a decision in this list (not counting nested branches). */
export function misplacedCount(steps: RuleStep[]): number {
	const first = steps.findIndex(isEnding);
	return first === -1 ? 0 : steps.length - 1 - first;
}

/**
 * Whether a change to a list keeps its order. A list that was already out of order (an old or
 * imported playbook) may change as long as it does not get worse, so it can be fixed step by step.
 */
export const keepsOrder = (before: RuleStep[], after: RuleStep[]): boolean => misplacedCount(after) <= misplacedCount(before);

/** A new step goes at the end, but an action goes just before the ending check or decision. */
export function insertStep(steps: RuleStep[], step: RuleStep): RuleStep[] {
	const at = steps.findIndex(isEnding);
	if (step.kind === 'action' && at !== -1) return [...steps.slice(0, at), step, ...steps.slice(at)];
	return [...steps, step];
}

export function moveStep<T>(items: T[], from: number, to: number): T[] {
	if (to < 0 || to >= items.length || from === to) return items;
	const next = [...items];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return next;
}

/** Whether a param is on screen: `showWhen` hides it until another param has a value. */
export function isParamVisible(param: CatalogParam, params: Record<string, unknown>): boolean {
	if (!param.showWhen) return true;
	return params[param.showWhen.key] === param.showWhen.equals;
}

/** Problems keyed by the step, condition or param they belong to (`<stepId>:<paramKey>`). */
export type RuleIssues = Record<string, string>;

const inRange = (value: unknown, param: CatalogParam): boolean =>
	typeof value === 'number' &&
	Number.isInteger(value) &&
	(param.min === undefined || value >= param.min) &&
	(param.max === undefined || value <= param.max);

function checkAction(step: ActionStep, catalog: RulesCatalog, trigger: RuleTrigger, issues: RuleIssues) {
	const action = catalog.actions.find((candidate) => candidate.id === step.action);
	if (!action) {
		issues[step.id] = 'Pick what to do.';
		return;
	}
	if (!action.triggers.includes(trigger)) issues[step.id] = 'This action is not available for this trigger.';
	for (const param of action.params) {
		if (!isParamVisible(param, step.params)) continue;
		const value = step.params[param.key];
		const empty = value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
		if (param.required && empty) issues[`${step.id}:${param.key}`] = `${param.label} is needed.`;
		else if (param.input === 'number' && !empty && !inRange(value, param)) {
			issues[`${step.id}:${param.key}`] = `Use a whole number from ${param.min ?? 0} to ${param.max ?? '…'}.`;
		}
		else if (param.input === 'email' && !empty && !EMAIL_RE.test(String(value).trim())) {
			issues[`${step.id}:${param.key}`] = 'That does not look like an email address.';
		}
	}
	// The reply's text is also what an AI reply falls back to, so it is needed in both modes.
	const text = step.params['text'];
	if (step.action === 'reply' && (typeof text !== 'string' || text.trim() === '')) {
		issues[`${step.id}:text`] = 'Write the reply. Kim also sends it if writing one takes too long.';
	}
}

function checkIf(step: IfStep, catalog: RulesCatalog, trigger: RuleTrigger, depth: number, issues: RuleIssues) {
	if (depth > MAX_IF_DEPTH) issues[step.id] = `Checks can sit at most ${MAX_IF_DEPTH} deep.`;
	if (step.conditions.length === 0) issues[step.id] = 'Add at least one condition.';
	for (const condition of step.conditions) {
		const fact = catalog.facts.find((candidate) => candidate.id === condition.fact);
		if (!fact) issues[condition.id] = 'Pick what to check.';
		else if (!fact.triggers.includes(trigger)) issues[condition.id] = 'This is not available for this trigger.';
		else if (!fact.operators.includes(condition.op)) issues[condition.id] = 'Pick how to compare.';
		else if (isValueMissing(valueInputFor(fact, condition.op, catalog.operators), condition.value)) issues[condition.id] = 'Add a value.';
	}
	if (step.then.length === 0 && !issues[step.id]) issues[step.id] = 'Add what happens when this is true.';
	checkList(step.then, catalog, trigger, depth, issues);
	checkList(step.else, catalog, trigger, depth, issues);
}

const AFTER_CHECK = 'Nothing can come after a check — move it into the If yes or Otherwise branch.';
const AFTER_DECISION = 'Nothing can come after a decision — it ends the playbook.';

function checkList(steps: RuleStep[], catalog: RulesCatalog, trigger: RuleTrigger, depth: number, issues: RuleIssues) {
	const ending = endingOf(steps);
	const endingIndex = ending ? steps.indexOf(ending) : -1;
	steps.forEach((step, index) => {
		if (step.kind === 'if') checkIf(step, catalog, trigger, depth + 1, issues);
		else if (step.kind === 'action') checkAction(step, catalog, trigger, issues);
		else if (!outcomesFor(catalog, trigger).some((outcome) => outcome.id === step.outcome)) {
			issues[step.id] = outcomesFor(catalog, trigger).length === 0
				? "This runs after it happened, so there's nothing to decide. Remove this step."
				: "That decision isn't available for this trigger.";
		}
		// Last, so the order problem is the one shown on the step: it is the one to fix first.
		if (ending && index > endingIndex) issues[step.id] = ending.kind === 'if' ? AFTER_CHECK : AFTER_DECISION;
	});
}

/** Everything that stops the playbook from saving. Empty means it is ready. */
export function validateRule(rule: RuleDefinition, catalog: RulesCatalog): RuleIssues {
	const issues: RuleIssues = {};
	if (!rule.name.trim()) issues['name'] = 'Give the playbook a name.';
	if (rule.steps.length === 0) issues['steps'] = 'Add at least one step.';
	checkList(rule.steps, catalog, rule.trigger, 0, issues);
	return issues;
}

/** Every step in the tree, depth first, for finding a step by its id. */
export function flattenSteps(steps: RuleStep[]): RuleStep[] {
	return steps.flatMap((step) => (step.kind === 'if' ? [step, ...flattenSteps(step.then), ...flattenSteps(step.else)] : [step]));
}
